import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Plus, 
  Trash2, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Printer, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Receipt,
  Briefcase,
  CheckCircle2,
  Sparkles,
  ArrowDownLeft,
  ArrowUpRight,
  Users,
  ArrowRight,
  AlertCircle
} from "lucide-react";
import Button from "../../components/common/Button";
import { Modal } from "../../components/common/Modal";
import { Input } from "../../components/common/Input";
import { 
  MonthlyTLedgerResponse, 
  LedgerItem, 
  ServiceProfitBreakdown,
  StaffPayoutBreakdown 
} from "../../services/jobService";
import {
  useMonthlyLedgerQuery,
  useCreateLedgerEntryMutation,
  useDeleteLedgerEntryMutation
} from "../../hooks/queries/useJobFinanceQueries";

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`cht-bg-white cht-border cht-border-gray-200/80 cht-rounded-[24px] cht-shadow-[0_10px_30px_rgba(0,0,0,0.03)] ${className}`}>
    {children}
  </div>
);

export const SuperAdminMonthlyLedger: React.FC = () => {
  const navigate = useNavigate();
  // Current month state (defaults to current YYYY-MM)
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });

  const createMutation = useCreateLedgerEntryMutation();
  const deleteMutation = useDeleteLedgerEntryMutation();

  const {
    data: ledgerData = null,
    isLoading: loading,
    refetch,
  } = useMonthlyLedgerQuery(selectedMonth);

  const [actionLoading, setActionLoading] = useState<boolean>(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"EXPENSE" | "INCOME">("EXPENSE");
  const [itemName, setItemName] = useState<string>("");
  const [itemAmount, setItemAmount] = useState<string>("");
  const [itemDate, setItemDate] = useState<string>("");
  const [itemNotes, setItemNotes] = useState<string>("");

  // Delete Prompt Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);

  // Status / Error Prompt Modal State
  const [promptMessage, setPromptMessage] = useState<{ title: string; message: string; type: "error" | "info" } | null>(null);

  // Month navigation helpers
  const handlePrevMonth = () => {
    const [yearStr, monStr] = selectedMonth.split("-");
    let year = parseInt(yearStr, 10);
    let mon = parseInt(monStr, 10) - 1;
    if (mon < 1) {
      mon = 12;
      year -= 1;
    }
    setSelectedMonth(`${year}-${String(mon).padStart(2, "0")}`);
  };

  const handleNextMonth = () => {
    const [yearStr, monStr] = selectedMonth.split("-");
    let year = parseInt(yearStr, 10);
    let mon = parseInt(monStr, 10) + 1;
    if (mon > 12) {
      mon = 1;
      year += 1;
    }
    setSelectedMonth(`${year}-${String(mon).padStart(2, "0")}`);
  };

  const monthFormattedTitle = useMemo(() => {
    try {
      const [year, mon] = selectedMonth.split("-");
      const d = new Date(parseInt(year, 10), parseInt(mon, 10) - 1, 1);
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    } catch {
      return selectedMonth;
    }
  }, [selectedMonth]);

  // Open modal with pre-configured type
  const openModal = (type: "EXPENSE" | "INCOME") => {
    setModalType(type);
    setItemName("");
    setItemAmount("");
    setItemNotes("");
    
    // Default date to today if inside selected month, else 1st of selected month
    const today = new Date().toISOString().split("T")[0];
    if (today.startsWith(selectedMonth)) {
      setItemDate(today);
    } else {
      setItemDate(`${selectedMonth}-01`);
    }
    setModalOpen(true);
  };

  // Submit new entry
  const handleSubmitEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemName || !itemAmount) return;

    try {
      setActionLoading(true);
      await createMutation.mutateAsync({
        entry_type: modalType,
        item_name: itemName,
        amount: itemAmount,
        date: itemDate || `${selectedMonth}-01`,
        notes: itemNotes,
      });
      setModalOpen(false);
    } catch (err) {
      console.error("Failed to add entry", err);
      setPromptMessage({
        title: "Save Failed",
        message: "Failed to save entry. Please verify amounts, inputs, and superadmin permissions.",
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Prompt delete modal
  const handlePromptDelete = (id: number | string, name: string) => {
    if (typeof id !== "number") return;
    setItemToDelete({ id, name });
    setDeleteModalOpen(true);
  };

  // Confirm delete manual entry
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setActionLoading(true);
      await deleteMutation.mutateAsync(itemToDelete.id);
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error("Failed to delete entry", err);
      setPromptMessage({
        title: "Delete Failed",
        message: "Unable to delete this entry. Please check your network and permissions.",
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Trigger browser print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700 cht-pb-16 print:cht-p-0 print:cht-space-y-4">
      {/* Top Header & Monthly Controls */}
      <header className="cht-flex cht-flex-col lg:cht-flex-row cht-items-start lg:cht-items-center cht-justify-between cht-gap-4 print:cht-hidden">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <div className="cht-p-2 cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-rounded-xl">
              <Receipt size={22} />
            </div>
            <div>
              <h1 className="cht-text-2xl lg:cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
                Income & Expenditure Account
              </h1>
              <p className="cht-text-xs cht-text-gray-500 cht-font-medium cht-mt-0.5">
                Two-column monthly statement matching your physical ledger notebook with automated staff job reconciliations.
              </p>
            </div>
          </div>
        </div>

        {/* Month Selector & Actions */}
        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2.5">
          {/* Month Stepper */}
          <div className="cht-flex cht-items-center cht-bg-white cht-border cht-border-gray-200 cht-rounded-2xl cht-p-1 cht-shadow-sm">
            <button
              onClick={handlePrevMonth}
              title="Previous Month"
              className="cht-p-2 cht-text-gray-500 hover:cht-text-[#3e57da] hover:cht-bg-gray-100 cht-rounded-xl cht-transition-all"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="cht-px-3 cht-flex cht-items-center cht-gap-2">
              <Calendar size={14} className="cht-text-[#3e57da]" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="cht-text-xs cht-font-black cht-text-gray-800 cht-bg-transparent cht-border-none cht-outline-none cursor-pointer"
              />
            </div>
            <button
              onClick={handleNextMonth}
              title="Next Month"
              className="cht-p-2 cht-text-gray-500 hover:cht-text-[#3e57da] hover:cht-bg-gray-100 cht-rounded-xl cht-transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Action Buttons */}
          <Button
            variant="outline"
            leftIcon={<Plus size={16} />}
            onClick={() => openModal("EXPENSE")}
            className="cht-rounded-2xl cht-border-rose-200 cht-text-rose-700 hover:cht-bg-rose-50 cht-text-xs"
          >
            + Expense
          </Button>

          <Button
            variant="outline"
            leftIcon={<Plus size={16} />}
            onClick={() => openModal("INCOME")}
            className="cht-rounded-2xl cht-border-emerald-200 cht-text-emerald-700 hover:cht-bg-emerald-50 cht-text-xs"
          >
            + Income
          </Button>

          <Button
            variant="secondary"
            leftIcon={<Printer size={16} />}
            onClick={handlePrint}
            className="cht-rounded-2xl cht-text-xs"
          >
            Print Sheet
          </Button>
        </div>
      </header>

      {/* Top 3 Metric Cards */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-5 print:cht-grid-cols-3">
        {/* Expenses Card */}
        <GlassCard className="cht-p-5 cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-rose-50 cht-text-rose-600 cht-rounded-2xl">
            <ArrowDownLeft size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Total Expenses
            </p>
            <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">
              ₦{Number(ledgerData?.totals?.total_expenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <span className="cht-text-[10px] cht-font-semibold cht-text-gray-500">
              Manual: ₦{Number(ledgerData?.totals?.manual_expenses_sum || 0).toLocaleString()} • Staff Payouts: ₦{Number(ledgerData?.totals?.staff_payout_expenditure || 0).toLocaleString()}
            </span>
          </div>
        </GlassCard>

        {/* Incomes Card */}
        <GlassCard className="cht-p-5 cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
            <ArrowUpRight size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Total Incomes
            </p>
            <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">
              ₦{Number(ledgerData?.totals?.total_incomes || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <span className="cht-text-[10px] cht-font-semibold cht-text-gray-500">
              Manual: ₦{Number(ledgerData?.totals?.manual_incomes_sum || 0).toLocaleString()} • Staff Jobs Net: ₦{Number(ledgerData?.totals?.total_job_net_revenue || ledgerData?.totals?.total_job_revenue || 0).toLocaleString()}
            </span>
          </div>
        </GlassCard>

        {/* Net Surplus / Deficit Card */}
        <GlassCard className="cht-p-5 cht-flex cht-items-center cht-gap-4">
          <div className={`cht-p-3.5 cht-rounded-2xl ${
            ledgerData?.totals?.is_surplus ? "cht-bg-emerald-50 cht-text-emerald-600" : "cht-bg-rose-50 cht-text-rose-600"
          }`}>
            <DollarSign size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Net {ledgerData?.totals?.is_surplus ? "Surplus (Profit)" : "Deficit (Loss)"}
            </p>
            <h3 className={`cht-text-xl cht-font-black cht-mt-0.5 ${
              ledgerData?.totals?.is_surplus ? "cht-text-emerald-600" : "cht-text-rose-600"
            }`}>
              {ledgerData?.totals?.is_surplus ? "+" : ""}₦{Number(ledgerData?.totals?.net_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <span className="cht-text-[10px] cht-font-semibold cht-text-gray-400">
              Financial Period: {monthFormattedTitle}
            </span>
          </div>
        </GlassCard>
      </div>

      {/* ── TWO-COLUMN NOTEBOOK SHEET ────────────────────────────────────────── */}
      <div className="cht-bg-white cht-rounded-[28px] cht-border-2 cht-border-gray-300 cht-shadow-md cht-overflow-hidden print:cht-border print:cht-rounded-none">
        {/* Notebook Title Bar */}
        <div className="cht-py-5 cht-border-b-2 cht-border-gray-300 cht-text-center cht-bg-gradient-to-r cht-from-gray-50/70 cht-via-white cht-to-gray-50/70">
          <h2 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-tracking-wide cht-font-serif">
            {monthFormattedTitle}
          </h2>
          <p className="cht-text-[11px] cht-font-bold cht-text-gray-400 cht-tracking-widest cht-uppercase cht-mt-0.5">
            Income & Expenditure Monthly Ledger Statement
          </p>
        </div>

        {/* 2-Column Split: Left = Expenses | Right = Incomes */}
        <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-divide-y md:cht-divide-y-0 md:cht-divide-x-2 cht-divide-gray-300">
          {/* ── LEFT COLUMN: EXPENSES ─────────────────────────────────────────── */}
          <div className="cht-flex cht-flex-col cht-justify-between cht-bg-[#fafbfe]/30">
            <div>
              {/* Column Header */}
              <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-bg-red-600 cht-text-white cht-border-b-2 cht-border-red-700">
                <span className="cht-text-sm cht-font-black cht-tracking-wider cht-uppercase">
                  Expenses / Expenditure
                </span>
                <span className="cht-text-base cht-font-black">₦</span>
              </div>

              {/* Rows List */}
              <div className="cht-divide-y cht-divide-gray-100/90">
                {ledgerData?.expenses && ledgerData.expenses.length > 0 ? (
                  ledgerData.expenses.map((exp: LedgerItem, idx: number) => (
                    <div
                      key={`exp-${exp.id}-${idx}`}
                      className="cht-group cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-3 hover:cht-bg-rose-50/20 cht-transition-colors"
                    >
                      <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                        <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-w-12 cht-shrink-0">
                          {exp.date}
                        </span>
                        <div className="cht-truncate">
                          <span className="cht-text-sm cht-font-bold cht-text-gray-800">
                            {exp.item}
                          </span>
                          {exp.is_system && (
                            <span className="cht-ml-2 cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-amber-50 cht-text-amber-700 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase">
                              <Sparkles size={10} />
                              Staff Payout
                            </span>
                          )}
                          {exp.notes && (
                            <span className="cht-block cht-text-[11px] cht-text-gray-400 cht-truncate">
                              {exp.notes}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
                        <span className="cht-text-sm cht-font-black cht-text-gray-900 cht-tabular-nums">
                          {Number(exp.amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                        </span>
                        {!exp.is_system && (
                          <button
                            onClick={() => handlePromptDelete(exp.id, exp.item)}
                            title="Delete entry"
                            className="cht-opacity-0 group-hover:cht-opacity-100 cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-lg cht-transition-all print:cht-hidden cursor-pointer"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="cht-py-12 cht-text-center cht-text-xs cht-text-gray-400 cht-font-medium">
                    No expense entries recorded for this month.
                    <div className="cht-mt-2 print:cht-hidden">
                      <Button
                        variant="outline"
                        onClick={() => openModal("EXPENSE")}
                        className="cht-rounded-xl cht-text-[11px] cht-py-1 cht-px-3"
                      >
                        + Add Expense
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Left Column Subtotal */}
            <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-border-t-2 cht-border-gray-300 cht-bg-gray-50/80 cht-mt-4">
              <span className="cht-text-xs cht-font-black cht-text-gray-700 cht-uppercase cht-tracking-wider">
                Total Expenses:
              </span>
              <span className="cht-text-base cht-font-black cht-text-rose-700 cht-tabular-nums">
                ₦{Number(ledgerData?.totals?.total_expenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* ── RIGHT COLUMN: INCOMES ─────────────────────────────────────────── */}
          <div className="cht-flex cht-flex-col cht-justify-between cht-bg-white">
            <div>
              {/* Column Header */}
              <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-bg-emerald-600 cht-text-white cht-border-b-2 cht-border-emerald-700">
                <span className="cht-text-sm cht-font-black cht-tracking-wider cht-uppercase">
                  Incomes
                </span>
                <span className="cht-text-base cht-font-black">₦</span>
              </div>

              {/* Rows List */}
              <div className="cht-divide-y cht-divide-gray-100/90">
                {ledgerData?.incomes && ledgerData.incomes.length > 0 ? (
                  ledgerData.incomes.map((inc: LedgerItem, idx: number) => (
                    <div
                      key={`inc-${inc.id}-${idx}`}
                      className="cht-group cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-3 hover:cht-bg-emerald-50/20 cht-transition-colors"
                    >
                      <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                        <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-w-12 cht-shrink-0">
                          {inc.date}
                        </span>
                        <div className="cht-truncate">
                          <span className="cht-text-sm cht-font-bold cht-text-gray-800">
                            {inc.item}
                          </span>
                          {inc.is_system && (
                            <span className="cht-ml-2 cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase">
                              <Sparkles size={10} />
                              Staff Jobs
                            </span>
                          )}
                          {inc.notes && (
                            <span className="cht-block cht-text-[11px] cht-text-gray-400 cht-truncate">
                              {inc.notes}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
                        <span className="cht-text-sm cht-font-black cht-text-gray-900 cht-tabular-nums">
                          {Number(inc.amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                        </span>
                        {!inc.is_system && (
                          <button
                            onClick={() => handlePromptDelete(inc.id, inc.item)}
                            title="Delete entry"
                            className="cht-opacity-0 group-hover:cht-opacity-100 cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-lg cht-transition-all print:cht-hidden cursor-pointer"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="cht-py-12 cht-text-center cht-text-xs cht-text-gray-400 cht-font-medium">
                    No income entries recorded for this month.
                    <div className="cht-mt-2 print:cht-hidden">
                      <Button
                        variant="outline"
                        onClick={() => openModal("INCOME")}
                        className="cht-rounded-xl cht-text-[11px] cht-py-1 cht-px-3"
                      >
                        + Add Income
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column Subtotal */}
            <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-border-t-2 cht-border-gray-300 cht-bg-gray-50/80 cht-mt-4">
              <span className="cht-text-xs cht-font-black cht-text-gray-700 cht-uppercase cht-tracking-wider">
                Total Incomes:
              </span>
              <span className="cht-text-base cht-font-black cht-text-emerald-700 cht-tabular-nums">
                ₦{Number(ledgerData?.totals?.total_incomes || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* ── FOOTER NET BALANCE BAR ────────────────────────────────────────── */}
        <div className={`cht-px-8 cht-py-5 cht-border-t-2 cht-border-gray-300 cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-3 ${
          ledgerData?.totals?.is_surplus ? "cht-bg-emerald-100/70" : "cht-bg-rose-100/70"
        }`}>
          <div>
            <span className="cht-text-xs cht-font-black cht-text-gray-800 cht-uppercase cht-tracking-widest">
              Net Operating Balance
            </span>
            <h4 className="cht-text-sm cht-font-bold cht-text-gray-600">
              {ledgerData?.totals?.is_surplus ? "Net Monthly Surplus (Profit)" : "Net Monthly Deficit (Loss)"}
            </h4>
          </div>

          <div className="cht-text-right">
            <span className={`cht-text-2xl lg:cht-text-3xl cht-font-black cht-tabular-nums ${
              ledgerData?.totals?.is_surplus ? "cht-text-emerald-800" : "cht-text-rose-800"
            }`}>
              {ledgerData?.totals?.is_surplus ? "+" : ""}₦{Number(ledgerData?.totals?.net_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* ── END OF MONTH STAFF JOB PROFIT BREAKDOWN ────────────────────────── */}
      <GlassCard className="cht-p-7 cht-rounded-[28px]">
        <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-2 cht-mb-6">
          <div>
            <div className="cht-flex cht-items-center cht-gap-2">
              <Briefcase size={18} className="cht-text-[#3e57da]" />
              <h3 className="cht-text-lg cht-font-black cht-text-gray-900">
                Staff Job Profit Breakdown by Service Type
              </h3>
            </div>
            <p className="cht-text-xs cht-text-gray-500 cht-font-medium cht-mt-0.5">
              Calculates monthly job volume, gross client payments, direct portal costs, staff commissions, and company net profit (Total of NERD, Affidavit, CAC, etc.).
            </p>
          </div>

          <div className="cht-flex cht-items-center cht-gap-2 cht-px-3 cht-py-1.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-full cht-text-xs cht-font-black">
            <CheckCircle2 size={14} />
            <span>Month-End Auto Calculation</span>
          </div>
        </div>

        <div className="cht-overflow-x-auto">
          <table className="cht-w-full cht-text-left">
            <thead>
              <tr className="cht-border-b cht-border-gray-200">
                <th className="cht-pb-3.5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Service Name
                </th>
                <th className="cht-pb-3.5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Jobs Executed
                </th>
                <th className="cht-pb-3.5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Total Billed (Gross)
                </th>
                <th className="cht-pb-3.5 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Direct Portal Cost
                </th>
                <th className="cht-pb-3.5 cht-text-[10px] cht-font-black cht-text-blue-700 cht-uppercase cht-tracking-wider">
                  Net Job Revenue (Incomes)
                </th>
                <th className="cht-pb-3.5 cht-text-[10px] cht-font-black cht-text-amber-700 cht-uppercase cht-tracking-wider">
                  Staff Commission
                </th>
                <th className="cht-pb-3.5 cht-text-[10px] cht-font-black cht-text-emerald-700 cht-uppercase cht-tracking-wider">
                  Company Net Profit
                </th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-100">
              {ledgerData?.service_profit_breakdown && ledgerData.service_profit_breakdown.length > 0 ? (
                ledgerData.service_profit_breakdown.map((s: ServiceProfitBreakdown) => {
                  const billedAmt = Number(s.billed || s.revenue);
                  const costAmt = Number(s.portal_cost);
                  const netRevAmt = Number(s.net_revenue || (billedAmt - costAmt));
                  return (
                    <tr key={s.service_key} className="hover:cht-bg-gray-50/60 cht-transition-colors">
                      <td className="cht-py-4 cht-text-sm cht-font-black cht-text-gray-900">
                        {s.service_label}
                      </td>
                      <td className="cht-py-4 cht-text-xs cht-font-bold cht-text-gray-600">
                        {s.count} {s.count === 1 ? "job" : "jobs"}
                      </td>
                      <td className="cht-py-4 cht-text-xs cht-font-black cht-text-gray-900 cht-tabular-nums">
                        ₦{billedAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="cht-py-4 cht-text-xs cht-font-bold cht-text-rose-600 cht-tabular-nums">
                        ₦{costAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="cht-py-4 cht-text-xs cht-font-black cht-text-[#3e57da] cht-tabular-nums">
                        ₦{netRevAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="cht-py-4 cht-text-xs cht-font-bold cht-text-amber-600 cht-tabular-nums">
                        ₦{Number(s.commission).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="cht-py-4 cht-text-sm cht-font-black cht-text-emerald-600 cht-tabular-nums">
                        ₦{Number(s.profit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="cht-py-8 cht-text-center cht-text-xs cht-text-gray-400 cht-font-medium">
                    No staff jobs logged for {monthFormattedTitle}.
                  </td>
                </tr>
              )}
            </tbody>
            {/* Table Footer Totals */}
            {ledgerData?.service_profit_breakdown && ledgerData.service_profit_breakdown.length > 0 && (
              <tfoot>
                <tr className="cht-border-t-2 cht-border-gray-200 cht-bg-gray-50/50 cht-font-black cht-text-xs">
                  <td className="cht-py-3.5 cht-text-gray-900">Total Across Services</td>
                  <td className="cht-py-3.5 cht-text-gray-700">
                    {ledgerData.service_profit_breakdown.reduce((acc, curr) => acc + curr.count, 0)} jobs
                  </td>
                  <td className="cht-py-3.5 cht-text-gray-900 cht-tabular-nums">
                    ₦{Number(ledgerData.totals.total_job_billed || ledgerData.totals.total_job_revenue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="cht-py-3.5 cht-text-rose-700 cht-tabular-nums">
                    ₦{Number(ledgerData.totals.total_job_cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="cht-py-3.5 cht-text-[#3e57da] cht-tabular-nums">
                    ₦{Number(ledgerData.totals.total_job_net_revenue || ledgerData.totals.total_job_revenue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="cht-py-3.5 cht-text-amber-700 cht-tabular-nums">
                    ₦{Number(ledgerData.totals.total_job_commission).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="cht-py-3.5 cht-text-emerald-700 cht-text-sm cht-tabular-nums">
                    ₦{Number(ledgerData.totals.total_job_profit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </GlassCard>

      {/* ── STAFF COMMISSION PAYOUT BREAKDOWN (EXPENDITURES) ────────────────── */}
      {ledgerData?.staff_payouts && ledgerData.staff_payouts.length > 0 && (
        <GlassCard className="cht-p-7 cht-rounded-[28px] cht-mt-6 print:cht-mt-4">
          <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-2 cht-mb-6">
            <div>
              <div className="cht-flex cht-items-center cht-gap-2">
                <Users size={18} className="cht-text-amber-600" />
                <h3 className="cht-text-lg cht-font-black cht-text-gray-900">
                  Staff Commission Payouts (Expenditures)
                </h3>
              </div>
              <p className="cht-text-xs cht-text-gray-500 cht-font-medium cht-mt-0.5">
                Individual commission payout amounts for each staff member, reflected in the Expenditure column.
              </p>
            </div>

            <div className="cht-flex cht-items-center cht-gap-2 print:cht-hidden">
              <Button
                variant="outline"
                onClick={() => navigate("/superadmin/dashboard/jobs/commissions")}
                className="cht-rounded-xl cht-text-xs cht-py-1.5 cht-px-3.5 cht-flex cht-items-center cht-gap-1.5"
              >
                <span>Manage Payroll & Disburse</span>
                <ArrowRight size={13} />
              </Button>
            </div>
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-4">
            {ledgerData.staff_payouts.map((staff: StaffPayoutBreakdown, idx: number) => (
              <div
                key={`staff-payout-${staff.staff_id || idx}`}
                className="cht-p-4 cht-rounded-2xl cht-border cht-border-gray-200/90 cht-bg-gray-50/50 cht-flex cht-items-center cht-justify-between"
              >
                <div className="cht-flex cht-items-center cht-gap-3">
                  <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-amber-100 cht-text-amber-800 cht-font-black cht-flex cht-items-center cht-justify-center cht-text-sm">
                    {staff.staff_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="cht-text-sm cht-font-black cht-text-gray-900">
                      {staff.staff_name}
                    </h4>
                    <span className="cht-text-[11px] cht-font-semibold cht-text-gray-400">
                      {staff.jobs_count} {staff.jobs_count === 1 ? "job executed" : "jobs executed"}
                    </span>
                  </div>
                </div>

                <div className="cht-text-right">
                  <span className="cht-text-sm cht-font-black cht-text-amber-700 cht-tabular-nums cht-block">
                    ₦{Number(staff.commission).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                  <span className={`cht-inline-block cht-text-[9px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-full ${
                    staff.is_disbursed ? "cht-bg-emerald-100 cht-text-emerald-800" : "cht-bg-amber-100 cht-text-amber-800"
                  }`}>
                    {staff.is_disbursed ? "Disbursed" : "Commission"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* ── MODAL: ADD EXPENSE OR INCOME ENTRY (Using Modal component) ──────── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        size="md"
        icon={modalType === "EXPENSE" ? <Receipt size={22} className="cht-text-rose-600" /> : <TrendingUp size={22} className="cht-text-emerald-600" />}
        title={`Add ${modalType === "EXPENSE" ? "Expense (Outflow)" : "Income (Inflow)"}`}
        subtitle={`Add a record to the ${modalType === "EXPENSE" ? "left (Expenditure)" : "right (Income)"} column for ${monthFormattedTitle}.`}
      >
        <div className="cht-space-y-4">
          {/* Type Switcher Pills */}
          <div className="cht-flex cht-items-center cht-justify-between cht-p-1.5 cht-bg-gray-100 cht-rounded-2xl">
            <span className="cht-text-xs cht-font-bold cht-text-gray-500 cht-pl-2">Entry Type:</span>
            <div className="cht-flex cht-gap-1">
              <button
                type="button"
                onClick={() => setModalType("EXPENSE")}
                className={`cht-px-4 cht-py-1.5 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all cursor-pointer ${
                  modalType === "EXPENSE" ? "cht-bg-rose-600 cht-text-white cht-shadow-sm" : "cht-text-gray-600 hover:cht-text-gray-900"
                }`}
              >
                Expense
              </button>
              <button
                type="button"
                onClick={() => setModalType("INCOME")}
                className={`cht-px-4 cht-py-1.5 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all cursor-pointer ${
                  modalType === "INCOME" ? "cht-bg-emerald-600 cht-text-white cht-shadow-sm" : "cht-text-gray-600 hover:cht-text-gray-900"
                }`}
              >
                Income
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmitEntry} className="cht-space-y-4">
            <Input
              label="Item Name / Description"
              required
              placeholder={modalType === "EXPENSE" ? "e.g. Data, Dustbin, Nerd Reg, Codehub LTD" : "e.g. Lena TIN, IN Card, Pst Mano Prints"}
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              leftIcon={modalType === "EXPENSE" ? <Receipt size={17} className="cht-text-rose-500" /> : <TrendingUp size={17} className="cht-text-emerald-500" />}
            />

            <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
              <Input
                label="Amount"
                type="number"
                required
                min="0"
                step="0.01"
                placeholder="e.g. 27700"
                value={itemAmount}
                onChange={(e) => setItemAmount(e.target.value)}
                prefixText="₦"
              />

              <Input
                label="Date"
                type="date"
                required
                value={itemDate}
                onChange={(e) => setItemDate(e.target.value)}
                leftIcon={<Calendar size={17} className="cht-text-gray-400" />}
              />
            </div>

            <Input
              label="Notes or Reference"
              subLabel="Optional"
              placeholder="e.g. Receipt #902, paid via transfer"
              value={itemNotes}
              onChange={(e) => setItemNotes(e.target.value)}
            />

            <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-pt-3 cht-border-t cht-border-gray-100">
              <Button
                variant="outline"
                type="button"
                onClick={() => setModalOpen(false)}
                className="cht-rounded-xl cht-text-xs"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={actionLoading}
                className={`cht-rounded-xl cht-text-xs ${
                  modalType === "EXPENSE" ? "cht-bg-rose-600 hover:cht-bg-rose-700" : "cht-bg-emerald-600 hover:cht-bg-emerald-700"
                }`}
              >
                {actionLoading ? "Saving..." : `Save ${modalType === "EXPENSE" ? "Expense" : "Income"}`}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* ── MODAL: DELETE CONFIRMATION PROMPT (Using Modal component) ──────── */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!actionLoading) {
            setDeleteModalOpen(false);
            setItemToDelete(null);
          }
        }}
        size="sm"
        icon={<Trash2 size={22} className="cht-text-rose-600" />}
        title="Confirm Removal"
        subtitle="Remove manual ledger record"
        footer={
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-w-full">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setDeleteModalOpen(false);
                setItemToDelete(null);
              }}
              disabled={actionLoading}
              className="cht-rounded-xl cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirmDelete}
              disabled={actionLoading}
              className="cht-rounded-xl cht-text-xs cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white"
            >
              {actionLoading ? "Removing..." : "Yes, Remove"}
            </Button>
          </div>
        }
      >
        <div className="cht-py-2">
          <p className="cht-text-sm cht-text-gray-700 cht-font-medium">
            Are you sure you want to remove <span className="cht-font-black cht-text-gray-900">"{itemToDelete?.name}"</span> from this month's ledger statement?
          </p>
          <p className="cht-text-xs cht-text-gray-400 cht-mt-2">
            This entry will be permanently deleted and the ledger totals will update immediately.
          </p>
        </div>
      </Modal>

      {/* ── MODAL: STATUS / ERROR PROMPT (Using Modal component) ───────────── */}
      <Modal
        isOpen={!!promptMessage}
        onClose={() => setPromptMessage(null)}
        size="sm"
        icon={<AlertCircle size={22} className={promptMessage?.type === "error" ? "cht-text-rose-600" : "cht-text-[#3e57da]"} />}
        title={promptMessage?.title || "Notification"}
        subtitle="System notification"
        footer={
          <div className="cht-flex cht-justify-end cht-w-full">
            <Button
              variant="primary"
              type="button"
              onClick={() => setPromptMessage(null)}
              className="cht-rounded-xl cht-text-xs"
            >
              Dismiss
            </Button>
          </div>
        }
      >
        <p className="cht-text-sm cht-text-gray-700 cht-font-medium cht-py-2">
          {promptMessage?.message}
        </p>
      </Modal>
    </div>
  );
};

export default SuperAdminMonthlyLedger;
